/**
 * CIRCAD number format.
 *
 * Dimensioned values are a bare decimal plus optional unit: `.100"`,
 * `25400µm`, `25.4mm`, `.06`. Angles carry `°`. No marker = the file's default
 * unit, inches in every stock library.
 *
 * Trailing zeroes and the leading `0` of `0.100` are stripped, matching the
 * original, so exports diff cleanly.
 */

import type { UnitsSetting } from './model.ts';

/** Inches per unit, for the names CIRCAD ships with. */
const UNIT_SCALES: Record<string, number> = {
  '"': 1,
  in: 1,
  mil: 1 / 1000,
  thou: 1 / 1000,
  µm: 1 / 25400,
  um: 1 / 25400,
  mm: 1 / 25.4,
  cm: 1 / 2.54,
  m: 1 / 0.0254,
};

/**
 * Parse a dimensioned value to inches.
 *
 * @param raw   token as written, e.g. `.100"` or `25.4mm`
 * @param units the doc's Units:: record — non-builtin names and default scale
 */
export function parseLength(raw: string, units?: UnitsSetting): number {
  const s = raw.trim();
  if (!s) return 0;

  // Fractions: `Radius:0.468/2"` = diameter over two. OG7.cpp:4608.
  // Stopping at the slash doubles the radius.
  const m = /^([+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?)(?:\/([+-]?(?:\d+\.?\d*|\.\d+)))?\s*(.*)$/
    .exec(s);
  if (!m) return 0;
  let value = parseFloat(m[1]!);
  if (m[2] !== undefined) {
    const denominator = parseFloat(m[2]);
    // Zero denominator ignored, not fatal — `if (!dd) dd = 1.0;`.
    if (denominator) value /= denominator;
  }
  const suffix = (m[3] ?? '').trim();

  if (!suffix) {
    // Bare number = file default unit.
    return units ? value * units.default : value;
  }

  const builtin = UNIT_SCALES[suffix];
  if (builtin !== undefined) return value * builtin;

  // Fall back to the doc unit table.
  const entry = units?.table.find((t) => t.name === suffix);
  if (entry && entry.scale !== 0) return value / entry.scale;

  return value;
}

/** Angle token (`67.5°`, `45`) to degrees. Takes p/q like lengths, OG7.cpp:4365. */
export function parseAngle(raw: string): number {
  const m = /^([+-]?(?:\d+\.?\d*|\.\d+))(?:\/([+-]?(?:\d+\.?\d*|\.\d+)))?/.exec(raw.trim());
  if (!m) return 0;
  const value = parseFloat(m[1]!);
  if (m[2] === undefined) return value;
  const denominator = parseFloat(m[2]);
  return denominator ? value / denominator : value;
}

/** CIRCAD number: fixed precision, trailing zeroes gone, bare `.` under 1. */
export function formatNumber(v: number, decimals = 6): string {
  if (!Number.isFinite(v)) return '0';
  let s = v.toFixed(decimals);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  if (s === '-0' || s === '') s = '0';
  // `0.100` → `.1`, `-0.25` → `-.25`
  s = s.replace(/^(-?)0\./, '$1.');
  return s;
}

/** Length in inches with the `"` marker, as the writer emits sizes. */
export function formatLength(inches: number, decimals = 6): string {
  return `${formatNumber(inches, decimals)}"`;
}

/** Angle with the degree sign the original emits (CP1252 0xB0). */
export function formatAngle(deg: number): string {
  return `${formatNumber(deg, 4)}°`;
}

/** Coordinate pair `x,y`. No unit — coordinates never carry one. */
export function formatPoint(x: number, y: number, decimals = 6): string {
  return `${formatNumber(x, decimals)},${formatNumber(y, decimals)}`;
}

// ---------------------------------------------------------------------------
// Display units — presentation only, storage stays inches
// ---------------------------------------------------------------------------

/** Shown unit. Storage is inches; changing this must not change the board. */
export type DisplayUnit = 'in' | 'mil' | 'mm';

/** Units per inch. Divide, never multiply by 1/25.4 — 25.4 × (1/25.4) ≠ 1. */
const PER_INCH: Record<DisplayUnit, number> = {
  in: 1,
  mil: 1000,
  mm: 25.4,
};

/** Suffix written after the number. */
const DISPLAY_SUFFIX: Record<DisplayUnit, string> = {
  in: '"',
  mil: ' mil',
  mm: ' mm',
};

/** Decimals per unit — equal fineness, all far below fab tolerance. */
const DISPLAY_DECIMALS: Record<DisplayUnit, number> = {
  in: 5,
  mil: 2,
  mm: 4,
};

export function toDisplay(inches: number, unit: DisplayUnit): number {
  return inches * PER_INCH[unit];
}

export function fromDisplay(value: number, unit: DisplayUnit): number {
  return value / PER_INCH[unit];
}

/** Unit name for a prompt or status field. */
export function unitName(unit: DisplayUnit): string {
  return unit === 'in' ? 'inches' : unit === 'mil' ? 'thou' : 'millimetres';
}

/** For showing: rounded, padded, with unit. Boxes want editValue. */
export function formatDisplay(inches: number, unit: DisplayUnit): string {
  return toDisplay(inches, unit).toFixed(DISPLAY_DECIMALS[unit]) + DISPLAY_SUFFIX[unit];
}

/**
 * For editing: whole number, no unit, no padding.
 *
 * Shown to the same precision the status bar shows — five decimals in inches,
 * four in millimetres, two in mils — and then trimmed of trailing zeros, so a
 * box reads `0.012` rather than `0.01200`.
 *
 * It used to keep twelve significant digits. That is fine for a value somebody
 * typed and unreadable for one that arrived from another program: an Altium
 * track width is 0.0059055118110236 inches, and a box asking you to edit
 * `0.00590551181102` is a box nobody can work with. The extra digits were never
 * meaningful either — they are the residue of converting a metric design to
 * inches, far below any tolerance a board is made to.
 *
 * Losing them costs nothing, because {@link readEdit} hands back the original
 * value untouched when the text has not been edited. The full precision
 * survives in the file; only the box is rounded.
 */
export function editValue(inches: number, unit: DisplayUnit): string {
  const v = toDisplay(inches, unit);
  if (!Number.isFinite(v)) return '0';
  return String(Number(v.toFixed(DISPLAY_DECIMALS[unit])));
}

/**
 * Read a box back. Unedited text returns `current` untouched — that identity
 * check is the point. Null on rubbish, so callers don't store NaN.
 */
export function readEdit(text: string, unit: DisplayUnit, current: number): number | null {
  const t = text.trim();
  if (!t) return null;
  if (t === editValue(current, unit)) return current;
  const v = parseFloat(t);
  if (!Number.isFinite(v)) return null;
  return fromDisplay(v, unit);
}
